/**
 * 演示模式模拟器
 *
 * 用途：评委演示时无需后端 WS 网关 + 多浏览器窗口，
 * 在教师监控页一键开启，模拟 3-5 个学员正在演练的完整场景。
 *
 * 模拟内容：
 * - 3-5 个学员同时开始演练（不同故障、不同难度）
 * - 每 1s 更新参数快照（模拟 DynamicsEngine tick，参数逐步漂移）
 * - 每 2-4s 随机一个学员做操作（正确/错误按 7:3 比例）
 * - 部分学员参数发散（divergingKeys 非空，触发红色告警）
 * - 30-60s 后陆续有学员结束演练
 * - 全部结束后自动重新开始新一轮
 *
 * 使用方式：
 *   const sim = new DemoSimulator();
 *   sim.start();  // 开始模拟，数据直接写入 realtimeStore
 *   sim.stop();   // 停止模拟，清空数据
 */

import { useRealtimeStore } from '@/stores/realtimeStore';

// ===== 模拟数据配置 =====

interface MockStudent {
  userId: string;
  display_name: string;
  sceneId: string;
  faultName: string;
  difficulty: string;
  // 参数模板：参数名 → { 正常值, 当前值, 漂移速率, 是否发散 }
  params: Record<string, { normal: number; current: number; driftRate: number; diverging: boolean; min: number; max: number }>;
  startedAt: number;
  // 预计演练时长（ms），到时间自动结束
  durationMs: number;
  // 操作序列（循环使用）
  actionSequence: Array<{ action: string; isCorrect: boolean }>;
  actionIndex: number;
  // 下次操作时间
  nextActionAt: number;
  ended: boolean;
}

const MOCK_STUDENT_NAMES = ['张明', '李华', '王强', '赵雪', '刘伟'];

const MOCK_FAULTS = [
  {
    sceneId: 'fcc',
    faultName: '反应温度异常升高',
    difficulty: 'standard',
    params: {
      'R-101:reactor_temp': { normal: 500, current: 535, driftRate: 0.3, diverging: true, min: 450, max: 580 },
      'REG-101:regenerator_temp': { normal: 690, current: 720, driftRate: 0.2, diverging: true, min: 650, max: 750 },
      'R-101:pressure': { normal: 2.0, current: 2.1, driftRate: 0, diverging: false, min: 1.5, max: 2.5 },
    },
    actions: [
      { action: '检查再生滑阀开度', isCorrect: true },
      { action: '适当关小再生滑阀', isCorrect: true },
      { action: '投用反应急冷油', isCorrect: true },
      { action: '提高原料预热温度', isCorrect: false },
      { action: '调整进料量', isCorrect: true },
    ],
  },
  {
    sceneId: 'tbm',
    faultName: '刀盘扭矩异常波动',
    difficulty: 'expert',
    params: {
      'TBM-01:rpm': { normal: 8, current: 7.2, driftRate: -0.05, diverging: true, min: 5, max: 10 },
      'TBM-01:torque': { normal: 3500, current: 4200, driftRate: 15, diverging: true, min: 2000, max: 5000 },
      'TBM-01:thrust': { normal: 8000, current: 8100, driftRate: 0, diverging: false, min: 6000, max: 10000 },
    },
    actions: [
      { action: '降低刀盘转速', isCorrect: true },
      { action: '检查掘进面地质', isCorrect: true },
      { action: '调整推进力', isCorrect: true },
      { action: '提高刀盘转速', isCorrect: false },
      { action: '检查刀盘磨损', isCorrect: true },
    ],
  },
  {
    sceneId: 'injection',
    faultName: '注塑压力不稳',
    difficulty: 'novice',
    params: {
      'INJ-01:inject_pressure': { normal: 120, current: 135, driftRate: 0.5, diverging: true, min: 80, max: 160 },
      'INJ-01:screw_temp': { normal: 200, current: 205, driftRate: 0.1, diverging: false, min: 180, max: 220 },
      'INJ-01:mold_temp': { normal: 60, current: 62, driftRate: 0, diverging: false, min: 40, max: 80 },
    },
    actions: [
      { action: '检查注塑压力设置', isCorrect: true },
      { action: '调整保压压力', isCorrect: true },
      { action: '检查模具温度', isCorrect: true },
      { action: '提高料筒温度', isCorrect: false },
      { action: '清理喷嘴', isCorrect: true },
    ],
  },
  {
    sceneId: 'welding',
    faultName: '焊接电流异常偏移',
    difficulty: 'standard',
    params: {
      'WLD-01:current': { normal: 220, current: 245, driftRate: 0.4, diverging: true, min: 150, max: 300 },
      'WLD-01:voltage': { normal: 24, current: 25, driftRate: 0, diverging: false, min: 20, max: 30 },
      'WLD-01:wire_speed': { normal: 8, current: 8.2, driftRate: 0, diverging: false, min: 5, max: 12 },
    },
    actions: [
      { action: '检查焊接电源', isCorrect: true },
      { action: '调整焊接电流', isCorrect: true },
      { action: '检查送丝速度', isCorrect: true },
      { action: '提高电弧电压', isCorrect: false },
      { action: '更换焊丝', isCorrect: true },
    ],
  },
  {
    sceneId: 'aluminum',
    faultName: '电解槽电压波动',
    difficulty: 'expert',
    params: {
      'AL-01:cell_voltage': { normal: 4.2, current: 4.6, driftRate: 0.01, diverging: true, min: 3.5, max: 5.0 },
      'AL-01:bath_temp': { normal: 950, current: 955, driftRate: 0.2, diverging: false, min: 900, max: 1000 },
      'AL-01:current_eff': { normal: 92, current: 88, driftRate: -0.1, diverging: true, min: 80, max: 95 },
    },
    actions: [
      { action: '检查阳极消耗', isCorrect: true },
      { action: '调整电解质成分', isCorrect: true },
      { action: '降低槽电压', isCorrect: true },
      { action: '提高电流强度', isCorrect: false },
      { action: '测量电解质温度', isCorrect: true },
    ],
  },
];

export class DemoSimulator {
  private students: MockStudent[] = [];
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private actionTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;

  /** 开始模拟 */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.initStudents();
    this.emitAllStarts();
    this.startTick();
    this.scheduleNextAction();
  }

  /** 停止模拟并清空数据 */
  stop(): void {
    this.running = false;
    if (this.tickTimer) { clearInterval(this.tickTimer); this.tickTimer = null; }
    if (this.actionTimer) { clearTimeout(this.actionTimer); this.actionTimer = null; }
    this.students = [];
    useRealtimeStore.getState().clearStudents();
  }

  isRunning(): boolean {
    return this.running;
  }

  // ===== 内部逻辑 =====

  /** 初始化 3-5 个模拟学员 */
  private initStudents(): void {
    const count = 3 + Math.floor(Math.random() * 3); // 3-5 人
    this.students = [];
    for (let i = 0; i < count; i++) {
      const fault = MOCK_FAULTS[i % MOCK_FAULTS.length];
      // 深拷贝参数模板
      const params: MockStudent['params'] = {};
      for (const [key, val] of Object.entries(fault.params)) {
        params[key] = { ...val };
      }
      this.students.push({
        userId: `demo-student-${i}`,
        display_name: MOCK_STUDENT_NAMES[i] ?? `学员${i + 1}`,
        sceneId: fault.sceneId,
        faultName: fault.faultName,
        difficulty: fault.difficulty,
        params,
        startedAt: Date.now(),
        durationMs: 30000 + Math.random() * 30000, // 30-60s
        actionSequence: fault.actions,
        actionIndex: 0,
        nextActionAt: Date.now() + 2000 + Math.random() * 3000, // 2-5s 后首次操作
        ended: false,
      });
    }
  }

  /** 发送所有学员的 start 消息 */
  private emitAllStarts(): void {
    const store = useRealtimeStore.getState();
    for (const s of this.students) {
      store.handleMessage({
        type: 'student:start',
        userId: s.userId,
        display_name: s.display_name,
        sceneId: s.sceneId,
        faultName: s.faultName,
        difficulty: s.difficulty,
        timestamp: s.startedAt,
      });
    }
  }

  /** 每 1s tick：更新参数快照 + 检查结束 */
  private startTick(): void {
    this.tickTimer = setInterval(() => {
      if (!this.running) return;
      const now = Date.now();
      const store = useRealtimeStore.getState();
      let allEnded = true;

      for (const s of this.students) {
        if (s.ended) continue;
        allEnded = false;

        // 检查是否到时间结束
        if (now - s.startedAt >= s.durationMs) {
          s.ended = true;
          const score = 60 + Math.floor(Math.random() * 35); // 60-94
          const grade = score >= 90 ? 'S' : score >= 80 ? 'A' : score >= 70 ? 'B' : score >= 60 ? 'C' : 'D';
          store.handleMessage({
            type: 'student:end',
            userId: s.userId,
            display_name: s.display_name,
            score,
            grade,
            timestamp: now,
          });
          continue;
        }

        // 参数漂移
        const snapshot: Record<string, number> = {};
        const divergingKeys: string[] = [];
        for (const [key, p] of Object.entries(s.params)) {
          // 漂移
          p.current += p.driftRate;
          // 钳制
          p.current = Math.max(p.min, Math.min(p.max, p.current));
          snapshot[key] = Math.round(p.current * 100) / 100;
          if (p.diverging) divergingKeys.push(key);
        }

        // 发送状态更新
        store.handleMessage({
          type: 'student:state',
          userId: s.userId,
          display_name: s.display_name,
          equipmentSnapshot: snapshot,
          divergingKeys,
        });
      }

      // 全部结束 → 3s 后重新开始一轮
      if (allEnded) {
        this.stop();
        setTimeout(() => { if (!this.running) this.start(); }, 3000);
      }
    }, 1000);
  }

  /** 调度下一次随机操作 */
  private scheduleNextAction(): void {
    if (!this.running) return;
    const delay = 1500 + Math.random() * 2500; // 1.5-4s
    this.actionTimer = setTimeout(() => {
      if (!this.running) return;
      this.emitRandomAction();
      this.scheduleNextAction();
    }, delay);
  }

  /** 随机选一个正在演练的学员做一次操作 */
  private emitRandomAction(): void {
    const active = this.students.filter((s) => !s.ended);
    if (active.length === 0) return;
    const s = active[Math.floor(Math.random() * active.length)];
    const action = s.actionSequence[s.actionIndex % s.actionSequence.length];
    s.actionIndex++;

    useRealtimeStore.getState().handleMessage({
      type: 'student:action',
      userId: s.userId,
      display_name: s.display_name,
      action: action.action,
      isCorrect: action.isCorrect,
      timestamp: Date.now(),
    });

    // 如果是正确操作，有概率让参数回归正常（模拟学员成功处置）
    if (action.isCorrect && Math.random() < 0.4) {
      for (const p of Object.values(s.params)) {
        if (p.diverging) {
          p.driftRate *= -0.5; // 反向漂移，向正常值回归
          if (Math.abs(p.current - p.normal) < 5) {
            p.diverging = false; // 回归正常后不再标红
          }
        }
      }
    }
  }
}

// ===== 单例 =====
let demoSimulator: DemoSimulator | null = null;

export function getDemoSimulator(): DemoSimulator {
  if (!demoSimulator) demoSimulator = new DemoSimulator();
  return demoSimulator;
}
